/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BRAND OPERATIONS (UPDATED FOR NEW SCHEMA)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Manages brand information with:
 * - Single mutable row per user (no versioning)
 * - UPSERT pattern (atomic create/update)
 * - Image hydration (logoId → base64 data)
 * - Multi-user isolation (RLS)
 * - Data validation (Zod schemas)
 *
 * SCHEMA CHANGE:
 * - OLD: Multiple snapshots per user with version history (immutable)
 * - NEW: One brand record per user (mutable, updated in place)
 *
 * INVOICE SNAPSHOTS:
 * - When invoice is created, brand data is COPIED into invoice as JSONB
 * - This table stores CURRENT brand info only
 * - Historical brand data lives in invoice.brand_snapshot JSONB column
 *
 * SOLVED ISSUES:
 * - Issue #1: ACID Transactions ✓ PostgreSQL UPSERT is atomic
 * - Issue #2: Counter Before Operation ✓ N/A (no version counters)
 * - Issue #3: Multi-Step Not Atomic ✓ Single UPSERT operation
 * - Issue #4: Export Not Atomic ✓ Single row per user (no export needed)
 * - Issue #5: Lock Timeout Race ✓ PostgreSQL handles concurrent UPSERTs
 * - Issue #6: Bulk Partial Failure ✓ N/A (single operation per user)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase, getCurrentUserId } from "../core/supabase.js";
import { getTimestamp } from "../core/utils.js";
import { brandSnapshotSchema, validateOrThrow } from "../validation/schemas.js";
import { requireFeature} from '../core/permissions.js';
import { FEATURES} from '../features/featureList.js';

// ══════════════════════════════════════════════════════════════════════════════
// BRAND OPERATIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Update brand information (create or update)
 * Called when user clicks "Save Brand Info"
 *
 * ✅ ATOMIC OPERATION: Uses PostgreSQL UPSERT (INSERT ... ON CONFLICT DO UPDATE)
 * ✅ MULTI-DEVICE SAFE: Concurrent updates from different devices handled correctly
 * ✅ VALIDATION: Zod schema validation before database write
 *
 * @param {object} brandData - Brand state from UI
 * @returns {Promise<object>} Updated brand info
 */
export async function updateBrandInfo(brandData) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 1: VALIDATE INPUT DATA
  // ────────────────────────────────────────────────────────────────────────

  if (!brandData || typeof brandData !== 'object') {
    throw new Error('brandData is required');
  }

  // Build brand data with defaults
  const brandInfo = {
    // Business info
    name: brandData.name || "",
    owner: brandData.owner || "",
    phone: brandData.phone || "",
    email: brandData.email || "",
    tagline: brandData.tagline || "",

    // Address
    addressLine1: brandData.addressLine1 || "",
    addressLine2: brandData.addressLine2 || "",
    city: brandData.city || "",
    state: brandData.state || "",
    country: brandData.country || "",
    zip: brandData.zip || "",

    // Tax info
    taxSystem: brandData.taxSystem || "NONE",
    taxId: brandData.taxId || "",
    canadaMode: brandData.canadaMode || "GST_PST",
    customTaxes: brandData.customTaxes || [],

    // Image IDs (references to images table or Supabase Storage URLs)
    logoId: brandData.logoId || null,
    signatureId: brandData.signatureId || null,
    watermarkType: brandData.watermarkType || "none",
    watermarkText: brandData.watermarkText || "",
    watermarkImageId: brandData.watermarkImageId || null
  };

  // ────────────────────────────────────────────────────────────────────────
  // STEP 2: VALIDATE AGAINST SCHEMA
  // ────────────────────────────────────────────────────────────────────────

  const validatedData = validateOrThrow(brandSnapshotSchema, brandInfo);

  // ────────────────────────────────────────────────────────────────────────
  // STEP 3: UPSERT TO DATABASE (ATOMIC)
  // ────────────────────────────────────────────────────────────────────────

  const userId = await getCurrentUserId();

  // Convert to database format (snake_case)
  const dbBrand = {
    user_id: userId,
    
    // Business info
    name: validatedData.name,
    owner: validatedData.owner,
    phone: validatedData.phone,
    email: validatedData.email,
    tagline: validatedData.tagline,

    // Address
    address_line1: validatedData.addressLine1,
    address_line2: validatedData.addressLine2,
    city: validatedData.city,
    state: validatedData.state,
    country: validatedData.country,
    zip: validatedData.zip,

    // Tax info
    tax_system: validatedData.taxSystem,
    tax_id: validatedData.taxId,
    canada_mode: validatedData.canadaMode,
    custom_taxes: validatedData.customTaxes,

    // Image IDs
    logo_id: validatedData.logoId,
    signature_id: validatedData.signatureId,
    watermark_type: validatedData.watermarkType,
    watermark_text: validatedData.watermarkText,
    watermark_image_id: validatedData.watermarkImageId,
    
    // Timestamp
    updated_at: getTimestamp()
  };

  // ✅ ATOMIC UPSERT: If row exists (user_id match), UPDATE; otherwise INSERT
  const { data, error } = await supabase
    .from('brand_snapshots')
    .upsert(dbBrand, { 
      onConflict: 'user_id',  // Primary key
      ignoreDuplicates: false  // Always update if exists
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to update brand info:', error);
    throw error;
  }

  console.log(`✓ Brand info updated for user ${userId}`);

  // Return in camelCase format
  return convertBrandToCamelCase(data);
}

/**
 * Get current brand information for user
 *
 * @param {object} options - { hydrate: boolean }
 * @returns {Promise<object|null>} Brand info or null if not found
 */
export async function getCurrentBrandInfo(options = {}) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const { hydrate = true } = options;

  const userId = await getCurrentUserId();

  // ✅ SIMPLE QUERY: One row per user (no joins, no version filtering)
  const { data, error } = await supabase
    .from('brand_snapshots')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to get brand info:', error);
    throw error;
  }

  if (!data) {
    console.debug(`No brand info found for user ${userId}`);
    return null;
  }

  const brand = convertBrandToCamelCase(data);

  // Hydrate with image data if requested
  if (hydrate) {
    return await hydrateBrandInfo(brand);
  }

  return brand;
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Convert database row (snake_case) to app format (camelCase)
 * @private
 */
function convertBrandToCamelCase(brand) {
  if (!brand) return null;

  return {
    userId: brand.user_id,
    updatedAt: brand.updated_at,

    // Business info
    name: brand.name,
    owner: brand.owner,
    phone: brand.phone,
    email: brand.email,
    tagline: brand.tagline,

    // Address
    addressLine1: brand.address_line1,
    addressLine2: brand.address_line2,
    city: brand.city,
    state: brand.state,
    country: brand.country,
    zip: brand.zip,

    // Tax info
    taxSystem: brand.tax_system,
    taxId: brand.tax_id,
    canadaMode: brand.canada_mode,
    customTaxes: brand.custom_taxes,

    // Image IDs
    logoId: brand.logo_id,
    signatureId: brand.signature_id,
    watermarkType: brand.watermark_type,
    watermarkText: brand.watermark_text,
    watermarkImageId: brand.watermark_image_id
  };
}

/**
 * Hydrate brand info with image data
 * @private
 */
async function hydrateBrandInfo(brand) {
  if (!brand) return null;

  try {
    // Import images operations (if available)
    const { getImageData } = await import("./images.js");

    return {
      ...brand,
      logo: await getImageData(brand.logoId),
      signature: await getImageData(brand.signatureId),
      watermarkImage: await getImageData(brand.watermarkImageId)
    };
  } catch (error) {
    console.warn('Failed to hydrate brand images:', error);
    // Return brand without hydration if images.js fails or images not found
    return {
      ...brand,
      logo: null,
      signature: null,
      watermarkImage: null
    };
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED FUNCTIONS (2 total):
 *
 * ✅ updateBrandInfo(brandData)
 *    - Creates or updates brand info for current user
 *    - Uses UPSERT pattern (atomic)
 *    - Returns updated brand info
 *
 * ✅ getCurrentBrandInfo(options)
 *    - Gets current brand info for user
 *    - Optional image hydration
 *    - Returns null if no brand info exists
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * REMOVED FUNCTIONS (13 deleted):
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ❌ createBrandSnapshot() - Replaced by updateBrandInfo()
 * ❌ getBrandSnapshot() - Replaced by getCurrentBrandInfo()
 * ❌ getLatestBrandSnapshot() - No longer needed (only one row exists)
 * ❌ getAllBrandSnapshots() - No snapshots to list
 * ❌ getBrandSnapshotsByVersionRange() - No versions
 * ❌ getBrandSnapshotCount() - Always 1 or 0
 * ❌ searchBrandSnapshots() - Only one row to search
 * ❌ deleteBrandSnapshot() - Use updateBrandInfo() with empty data if needed
 * ❌ cleanupOldBrandSnapshots() - No old snapshots
 * ❌ getBrandSnapshotStats() - No stats needed
 * ❌ verifyBrandSnapshotIntegrity() - No integrity checks needed
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ALL ISSUES SOLVED:
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ✅ Issue #1: ACID Transactions
 *    - PostgreSQL UPSERT is atomic (single operation)
 *
 * ✅ Issue #2: Counter Before Operation
 *    - N/A (no version counters in new schema)
 *
 * ✅ Issue #3: Multi-Step Not Atomic
 *    - Single UPSERT operation (no multi-step workflow)
 *
 * ✅ Issue #4: Export Not Atomic
 *    - N/A (single row per user, no batch export)
 *
 * ✅ Issue #5: Lock Timeout Race
 *    - PostgreSQL handles concurrent UPSERTs correctly
 *    - Row-level locking prevents data corruption
 *
 * ✅ Issue #6: Bulk Partial Failure
 *    - N/A (single operation per user)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */